import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { PreflightCopy } from '@signalvote/shared';
import type { ComplianceReportDetailDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useDismissReceiptChase } from '@/hooks/queries/useFinanceReports';
import { ReceiptEditor } from './ReceiptEditor';
import { withReceivingOffice } from './receivingOffice';

interface ReceiptChaseOpenItemProps {
  report: ComplianceReportDetailDto;
  /** The served receiving-office name (`profile.portalName` — "Secretary of State" / "Circuit Clerk"). */
  officeName: string;
}

/**
 * The receipt chase. Where a jurisdiction's receipt is the only proof of filing (Mississippi), the
 * receipt is optional at confirm-filed so a treasurer is never blocked from marking a timely filing —
 * which makes "confirm now, never come back" the predictable path. This open item stays on the report
 * (server-derived `receiptChaseOpen`) until the receipt is recorded here or the treasurer explicitly
 * dismisses it; the dismissal is a recorded decision, so it takes a second, deliberate click.
 */
export function ReceiptChaseOpenItem({ report, officeName }: ReceiptChaseOpenItemProps) {
  const { dismissReceiptChase } = useDismissReceiptChase(report.id);
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const withOffice = (copy: string) => withReceivingOffice(copy, officeName);

  const handleDismiss = async () => {
    setDismissing(true);
    setError(null);
    try {
      await dismissReceiptChase({ reportId: report.id, concurrencyVersion: report.concurrencyVersion });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to dismiss the reminder');
    } finally {
      setDismissing(false);
      setConfirmingDismiss(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3"
      data-testid="receipt-chase-open-item"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-semibold text-amber-900">{withOffice(PreflightCopy.ReceiptChaseTitle)}</p>
          <p className="text-sm text-amber-900">{withOffice(PreflightCopy.ReceiptChaseBody)}</p>
        </div>
      </div>
      <ReceiptEditor
        reportId={report.id}
        receiptId={report.stateFilingReceiptId}
        receiptAt={report.stateFilingReceiptAt}
        concurrencyVersion={report.concurrencyVersion}
      />
      {confirmingDismiss ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-amber-900">{PreflightCopy.ReceiptChaseDismissConfirm}</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleDismiss()}
            disabled={dismissing}
            data-testid="receipt-chase-dismiss-confirm"
          >
            Yes, stop reminding me
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmingDismiss(false)} disabled={dismissing}>
            Keep the reminder
          </Button>
        </div>
      ) : (
        <button
          type="button"
          className="text-xs text-amber-800 underline hover:text-amber-900"
          onClick={() => setConfirmingDismiss(true)}
          data-testid="receipt-chase-dismiss"
        >
          {PreflightCopy.ReceiptChaseDismiss}
        </button>
      )}
      {error && (
        <p className="text-sm text-destructive" data-testid="receipt-chase-error">
          {error}
        </p>
      )}
    </div>
  );
}
